import { z } from 'zod';

// Shared fill contract for composed (GENERATED) ticket pools. Serialized (via toFillString) into the
// backend's CreateGeneratedTicketPoolView.fill as a JSON string:
//   {"bannerSource":"event"|"custom"|"fangroup"|"none","bannerFileId":"<id>?","instructions":{"<slotKey>":"<html>",...}}
// 'event' uses the backend event image, 'custom' uses bannerFileId, 'none' = no banner.
// 'fangroup' carries NO bannerFileId: the fan-group entity exposes only served image URLs, never
// the bare FileId, so the backend stamps in the group's own image id at pool creation
// (TicketFill.withFanGroupImage) rather than have the client guess it from a URL.
// `instructions` is the FGO's per-slot authored HTML (rich-text, sanitised server-side at pool
// creation — see GeneratedTicketContentSanitizer). Keys are the chosen design's slot keys (SlotKey in
// the composer's types.ts); omitted, not defaulted, when a slot has no content — mirrors how
// bannerFileId is already omitted-when-absent. Kept as a plain string-keyed record rather than a
// SlotKey enum, since a design never populates all four keys (single-column uses only 'primary') and
// this schema shouldn't need to know every design's slot set.
// Parsed on the backend by TicketFill.parse and GeneratedTicketBanner.resolve, built by
// ComposeTicketsFlow.tsx's buildTicketFillPayload — keep the field names and source values in
// lockstep with both.
export const TicketFillSchema = z
  .object({
    bannerSource: z.enum(['event', 'custom', 'fangroup', 'none']),
    bannerFileId: z.string().optional(),
    instructions: z.record(z.string().min(1), z.string()).optional(),
  })
  .superRefine((fill, ctx) => {
    // Only 'custom' renders from a client-supplied bannerFileId, and a missing OR blank id there has
    // no image to load — GeneratedTicketBanner treats a blank id as "fall back to the event image",
    // so require a non-blank one rather than emit a fill that renders the wrong banner.
    // 'fangroup' is deliberately exempt: its id is filled in server-side.
    if (fill.bannerSource === 'custom' && !fill.bannerFileId?.trim()) {
      ctx.addIssue({
        message: 'validation_banner_file_required',
        code: z.ZodIssueCode.custom,
        path: ['bannerFileId'],
      });
    }
  });

export type TicketFill = z.infer<typeof TicketFillSchema>;

// Serializer for CreateGeneratedTicketPoolView.fill. ComposeTicketsFlow.tsx builds `fill` through this
// so the on-the-wire string stays consistent with the backend's TicketFill.parse. JSON.stringify drops
// an undefined bannerFileId/instructions, so a banner-only fill serializes to just {"bannerSource":...}.
export const toFillString = (fill: TicketFill): string => JSON.stringify(fill);

// Inverse of toFillString, for reading a stored pool's fill back into the composer (duplicating a
// pool — #1652). Validated through the same schema rather than cast, so a fill written by an older
// client, or hand-edited server-side, cannot smuggle an unknown bannerSource into the editor.
// Returns null on malformed JSON or a shape the schema rejects; callers fall back to their defaults.
export const fromFillString = (fill: string): TicketFill | null => {
  try {
    const parsed = TicketFillSchema.safeParse(JSON.parse(fill));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};
